/*v3* ==========================================================================
   CORLIS ATELIER — theme behaviour
   Replaces the prototype runtime with plain browser JavaScript.
   No framework, no build step, no prototype state classes.
   ========================================================================== */

(function () {
  'use strict';

  var focusable = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  var lastFocused = null;

  /* ---------------- Overlay helpers ---------------- */

  function openOverlay(el) {
    if (!el) return;
    lastFocused = document.activeElement;
    el.classList.add('is-open');
    el.removeAttribute('hidden');
    document.body.classList.add('corlis-no-scroll');
    var first = el.querySelector(focusable);
    if (first) first.focus();
    trapFocus(el);
  }

  function closeOverlay(el) {
    if (!el) return;
    el.classList.remove('is-open');
    el.setAttribute('hidden', '');
    document.body.classList.remove('corlis-no-scroll');
    releaseFocus(el);
    if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
  }

  function trapFocus(el) {
    el._trap = function (e) {
      if (e.key !== 'Tab') return;
      var items = Array.prototype.slice.call(el.querySelectorAll(focusable)).filter(function (n) {
        return n.offsetParent !== null;
      });
      if (!items.length) return;
      var first = items[0], last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    el.addEventListener('keydown', el._trap);
  }

  function releaseFocus(el) {
    if (el._trap) { el.removeEventListener('keydown', el._trap); el._trap = null; }
  }

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    var open = document.querySelector('.overlay.is-open');
    if (open) closeOverlay(open);
  });

  /* Wire every [data-overlay-open] / [data-overlay-close] pair */
  document.addEventListener('click', function (e) {
    var opener = e.target.closest('[data-overlay-open]');
    if (opener) {
      e.preventDefault();
      openOverlay(document.getElementById(opener.getAttribute('data-overlay-open')));
      return;
    }
    var closer = e.target.closest('[data-overlay-close]');
    if (closer) {
      e.preventDefault();
      closeOverlay(closer.closest('.overlay'));
    }
  });

  /* ---------------- Sticky masthead ----------------
     Shopify owns the section wrapper; keep that wrapper sticky and measure
     its visible header so the homepage overlap also fits changing fonts.
  ------------------------------------------------------------------------ */

  var headerSection = null;
  var transparentHeader = null;
  var headerResizeObserver = null;

  function updateHeaderScroll() {
    if (transparentHeader) {
      transparentHeader.classList.toggle('is-scrolled', window.scrollY > 40);
    }
  }

  function measureHeader() {
    if (!headerSection) return;
    var height = Math.ceil(headerSection.getBoundingClientRect().height);
    if (height > 0) {
      document.documentElement.style.setProperty('--corlis-header-height', height + 'px');
    }
  }

  function initHeader() {
    if (headerResizeObserver) headerResizeObserver.disconnect();
    headerSection = document.querySelector('.corlis-header-section');
    transparentHeader = headerSection && headerSection.querySelector('.header--transparent');
    measureHeader();
    updateHeaderScroll();
    if (headerSection && 'ResizeObserver' in window) {
      headerResizeObserver = new ResizeObserver(measureHeader);
      headerResizeObserver.observe(headerSection);
    }
  }

  initHeader();
  window.addEventListener('scroll', updateHeaderScroll, { passive: true });
  window.addEventListener('resize', measureHeader, { passive: true });
  window.addEventListener('pageshow', initHeader);
  document.addEventListener('shopify:section:load', initHeader);

  /* ---------------- Cookie preferences ----------------
     Shopify's native banner owns consent collection and persistence.
     Keep the existing footer button connected to its preferences dialog.
  -------------------------------------------------------------------- */
  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-cookie-action="manage"]');
    if (!button) return;
    event.preventDefault();
    if (button.disabled) return;

    var message = document.getElementById('corlis-cookie-preferences-error');
    if (!message) {
      message = document.createElement('p');
      message.id = 'corlis-cookie-preferences-error';
      message.setAttribute('role', 'alert');
      message.hidden = true;
      button.insertAdjacentElement('afterend', message);
    }
    message.hidden = true;

    function showError() {
      message.textContent = 'Cookie preferences could not be opened. Please try again or reload this page.';
      message.hidden = false;
    }

    if (!window.privacyBanner || typeof window.privacyBanner.showPreferences !== 'function') {
      showError();
      return;
    }

    button.disabled = true;
    var settled = false;
    var timer = setTimeout(function () { finish(new Error('Preferences timed out')); }, 10000);
    function finish(error) {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      button.disabled = false;
      if (error) showError();
    }
    try {
      Promise.resolve(window.privacyBanner.showPreferences()).then(
        function () { finish(null); },
        function (error) { finish(error || new Error('Preferences unavailable')); }
      );
    } catch (error) {
      finish(error);
    }
  });

  /* ---------------- Newsletter inline validation ---------------- */

  Array.prototype.forEach.call(document.querySelectorAll('.newsletter__form'), function (form) {
    var input = form.querySelector('input[type="email"]');
    var error = form.querySelector('[data-newsletter-error]');
    if (!input) return;
    form.addEventListener('submit', function (e) {
      var value = (input.value || '').trim();
      var valid = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value);
      if (!valid) {
        e.preventDefault();
        input.setAttribute('aria-invalid', 'true');
        if (error) error.hidden = false;
        input.focus();
      } else {
        input.removeAttribute('aria-invalid');
        if (error) error.hidden = true;
      }
    });
    input.addEventListener('input', function () {
      input.removeAttribute('aria-invalid');
      if (error) error.hidden = true;
    });
  });

  /* ---------------- Accordions ---------------- */
  Array.prototype.forEach.call(document.querySelectorAll('.accordion__trigger'), function (trigger) {
    trigger.addEventListener('click', function () {
      var expanded = trigger.getAttribute('aria-expanded') === 'true';
      trigger.setAttribute('aria-expanded', String(!expanded));
      var panel = document.getElementById(trigger.getAttribute('aria-controls'));
      if (panel) panel.hidden = expanded;
    });
  });

  /* ---------------- Product gallery ---------------- */

  Array.prototype.forEach.call(document.querySelectorAll('[data-gallery]'), function (gallery) {
    var main = gallery.querySelector('[data-gallery-main]');
    if (!main) return;
    gallery.addEventListener('click', function (e) {
      var thumb = e.target.closest('[data-gallery-thumb]');
      if (!thumb) return;
      var src = thumb.getAttribute('data-src');
      var alt = thumb.getAttribute('data-alt') || '';
      if (src) { main.setAttribute('src', src); main.setAttribute('alt', alt); }
      Array.prototype.forEach.call(gallery.querySelectorAll('[data-gallery-thumb]'), function (t) {
        t.classList.toggle('is-active', t === thumb);
        t.setAttribute('aria-current', t === thumb ? 'true' : 'false');
      });
    });
  });

  /* ---------------- Quantity stepper ---------------- */

  Array.prototype.forEach.call(document.querySelectorAll('[data-qty]'), function (wrap) {
    var input = wrap.querySelector('input');
    wrap.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-qty-step]');
      if (!btn || !input) return;
      var step = parseInt(btn.getAttribute('data-qty-step'), 10);
      var next = Math.max(1, (parseInt(input.value, 10) || 1) + step);
      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  });

})();
/* v13 - back to top (homepage only; the button is rendered by layout/theme.liquid on the index template) */
(function () { var b = document.getElementById('back-to-top'); if (!b) return; var hero = document.querySelector('.hero'); var reduce = window.matchMedia('(prefers-reduced-motion: reduce)'); function update() { var limit = hero ? hero.getBoundingClientRect().bottom + window.scrollY : 600; b.classList.toggle('is-visible', window.scrollY > limit - 40); } window.addEventListener('scroll', update, { passive: true }); window.addEventListener('resize', update); update(); b.addEventListener('click', function () { window.scrollTo({ top: 0, behavior: reduce.matches ? 'auto' : 'smooth' }); var first = document.querySelector('header a.logo'); if (first && first.offsetParent) { first.focus({ preventScroll: true }); } else { b.blur(); } }); })();

